/**
 * FocusTryOn — a small composited preview of an item worn on the base avatar,
 * cropped + zoomed to the relevant body region (hat → head, boots → feet …).
 * Static; used inside shop cells. Crop is computed in pixels off the measured size.
 */

import { useMemo, useState } from 'react';
import { View, type LayoutChangeEvent } from 'react-native';
import { Image } from 'expo-image';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../src/shared/helpers/imageWebAlt.helper';
import { getCustomizerAssetUrl } from './characterCustomizer.assets';
import { focusFor, getFocusBox, itemOf } from './characterCustomizer.helper';
import { focusTryOnStyles as styles, getCropBoxStyle } from './FocusTryOn.styles';
import type { CustomizerCategoryId } from './characterCustomizer.types';

interface FocusTryOnProps {
  category: CustomizerCategoryId;
  /** Item to show; null previews the bare mannequin (used by the "None" cell). */
  id: string | null;
  mannequinId?: string;
}

export function FocusTryOn({ category, id, mannequinId = 'striker' }: FocusTryOnProps) {
  const [side, setSide] = useState(0);

  const urls = useMemo(() => {
    const baseId = category === 'base' ? id ?? mannequinId : mannequinId;
    const out: { key: string; url: string; dimmed: boolean }[] = [];
    const base = itemOf('base', baseId);
    if (base) {
      out.push({
        key: 'base',
        url: getCustomizerAssetUrl(base.assetKey),
        dimmed: category !== 'base',
      });
    }
    if (category !== 'base' && id) {
      const it = itemOf(category, id);
      if (it) out.push({ key: category, url: getCustomizerAssetUrl(it.assetKey), dimmed: false });
    }
    return out;
  }, [category, id, mannequinId]);

  const box = useMemo(() => getFocusBox(focusFor(category), side), [category, side]);

  const onLayout = (e: LayoutChangeEvent) => {
    const next = e.nativeEvent.layout.width;
    if (next > 0 && next !== side) setSide(next);
  };

  return (
    <View style={styles.root} onLayout={onLayout}>
      {side > 0 ? (
        <View style={getCropBoxStyle(box)}>
          {urls.map((layer) => (
            <Image
              key={layer.key}
              source={{ uri: layer.url }}
              style={[styles.layer, layer.dimmed ? styles.layerDimmed : null]}
              contentFit="contain"
              transition={120}
              alt={WEB_IMAGE_ALT_DECORATIVE}
            />
          ))}
        </View>
      ) : null}
    </View>
  );
}
